import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { ContactErrors, ContactField, ContactValues } from '@/lib/contact'

import {
    ArrowUpRight,
    Check,
    ChevronDown,
    LoaderCircle
} from 'lucide-react'

import {
    useEffect,
    useId,
    useRef,
    useState
} from 'react'

import { cn } from '@/lib/utils'

import {
    contactTopics,
    emptyContact,
    validateContact
} from '@/lib/contact'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const FIELD_ORDER: ContactField[] = ['name', 'email', 'phone', 'topic', 'message']

const fieldClass = (invalid: boolean): string => cn(
    'mt-2 h-12 w-full rounded-none border-0 border-b bg-transparent px-0 text-base text-charcoal outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-charcoal/35 focus:border-forest focus:shadow-[inset_0_-1px_0_0_#1e3a2c]',
    invalid ? 'border-destructive' : 'border-charcoal/25'
)

/** The contact form. Messages are stored in the contact_messages collection of the CMS. */
export const ContactForm: FC = (): ReactNode => {
    const baseId = useId()
    const honeypotRef = useRef<HTMLInputElement>(null)
    const successRef = useRef<HTMLHeadingElement>(null)

    const [values, setValues] = useState<ContactValues>(() => emptyContact())
    const [errors, setErrors] = useState<ContactErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverError, setServerError] = useState('')
    const [reference, setReference] = useState('')

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const fieldId = (field: ContactField): string => `${baseId}-${field}`
    const describedBy = (field: ContactField): string | undefined => (errors[field] ? `${fieldId(field)}-error` : undefined)

    const update = (field: ContactField) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
        const { value } = event.target
        setValues((previous) => ({ ...previous, [field]: value }))

        if (errors[field]) {
            setErrors((previous) => {
                const next = { ...previous }
                delete next[field]
                return next
            })
        }
    }

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()
        if (status === 'submitting') return

        const found = validateContact(values)
        setErrors(found)

        if (Object.keys(found).length > 0) {
            setStatus('idle')
            const first = FIELD_ORDER.find((field) => found[field])
            if (first) document.getElementById(fieldId(first))?.focus()
            return
        }

        setStatus('submitting')
        setServerError('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, company: honeypotRef.current?.value ?? '' })
            })

            const data = (await response.json().catch(() => null)) as
                | { ok: true, reference: string }
                | { ok: false, error?: string, errors?: ContactErrors }
                | null

            if (!response.ok || !data || !data.ok) {
                if (data && !data.ok && data.errors) setErrors(data.errors)
                setServerError(data && !data.ok && data.error ? data.error : 'Something went wrong. Please try again.')
                setStatus('error')
                return
            }

            setReference(data.reference)
            setStatus('success')
        } catch {
            setServerError('We couldn’t reach the concierge just now. Please check your connection and try again.')
            setStatus('error')
        }
    }

    const reset = (): void => {
        setValues(emptyContact())
        setErrors({})
        setServerError('')
        setReference('')
        setStatus('idle')
    }

    const errorText = (field: ContactField): ReactNode => errors[field] && (
        <p id={`${fieldId(field)}-error`} className='mt-2 text-xs text-destructive'>
            {errors[field]}
        </p>
    )

    const label = (field: ContactField, text: string): ReactNode => (
        <label htmlFor={fieldId(field)} className='font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/60'>
            {text}
        </label>
    )

    if (status === 'success') {
        const firstName = values.name.split(' ')[0]

        return (
            <div role='status' className='flex min-h-[30rem] flex-col justify-between gap-10 animate-in fade-in slide-in-from-bottom-4 duration-700'>
                <div>
                    <span className='flex size-14 items-center justify-center rounded-full bg-forest text-bone'>
                        <Check className='size-6' aria-hidden='true' />
                    </span>

                    <h3 ref={successRef} tabIndex={-1} className='type-wide mt-8 text-[clamp(1.6rem,2.6vw,2.2rem)] font-medium uppercase leading-tight outline-none'>
                        Message received
                    </h3>

                    <p className='mt-4 max-w-md text-base leading-relaxed text-charcoal/75'>
                        Thank you{firstName ? `, ${firstName}` : ''}. Someone from the team will reply to <span className='font-medium text-charcoal'>{values.email}</span> within one working day.
                    </p>

                    <dl className='mt-8 inline-flex flex-col rounded-[4px] border border-charcoal/15 px-5 py-4'>
                        <dt className='font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/55'>
                            Your reference
                        </dt>

                        <dd className='type-wide mt-1 text-xl font-medium tracking-[0.08em]'>
                            {reference}
                        </dd>
                    </dl>
                </div>

                <button
                    type='button'
                    onClick={reset}
                    className='inline-flex h-12 w-fit items-center gap-3 rounded-full border border-charcoal/20 px-6 text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors hover:border-charcoal hover:bg-charcoal hover:text-bone'
                >
                    Send another message
                </button>
            </div>
        )
    }

    return (
        <form noValidate onSubmit={submit} aria-labelledby={`${baseId}-heading`} className='relative'>
            <p className='font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55'>
                Write to us
            </p>

            <h3 id={`${baseId}-heading`} className='type-wide mt-3 text-[clamp(1.6rem,2.6vw,2.2rem)] font-medium uppercase leading-tight'>
                Send a message
            </h3>

            <p className='mt-3 max-w-lg text-[15px] leading-relaxed text-charcoal/70'>
                For tee times, lessons, dining and stays you can also use the booking request, which reaches the concierge directly.
            </p>

            <div className='mt-10 grid gap-x-6 gap-y-7 sm:grid-cols-2'>
                <div className='flex flex-col'>
                    {label('name', 'Full name')}
                    <input
                        id={fieldId('name')}
                        type='text'
                        autoComplete='name'
                        value={values.name}
                        onChange={update('name')}
                        required
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={describedBy('name')}
                        className={fieldClass(Boolean(errors.name))}
                    />
                    {errorText('name')}
                </div>

                <div className='flex flex-col'>
                    {label('email', 'Email')}
                    <input
                        id={fieldId('email')}
                        type='email'
                        autoComplete='email'
                        value={values.email}
                        onChange={update('email')}
                        required
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy('email')}
                        className={fieldClass(Boolean(errors.email))}
                    />
                    {errorText('email')}
                </div>

                <div className='flex flex-col'>
                    {label('phone', 'Phone (optional)')}
                    <input
                        id={fieldId('phone')}
                        type='tel'
                        autoComplete='tel'
                        value={values.phone}
                        onChange={update('phone')}
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={describedBy('phone')}
                        className={fieldClass(Boolean(errors.phone))}
                    />
                    {errorText('phone')}
                </div>

                <div className='flex flex-col'>
                    {label('topic', 'Topic')}
                    <div className='relative'>
                        <select
                            id={fieldId('topic')}
                            value={values.topic}
                            onChange={update('topic')}
                            aria-invalid={Boolean(errors.topic)}
                            aria-describedby={describedBy('topic')}
                            className={cn(fieldClass(Boolean(errors.topic)), 'cursor-pointer appearance-none pr-8')}
                        >
                            {contactTopics.map((topic) => (
                                <option key={topic} value={topic}>
                                    {topic}
                                </option>
                            ))}
                        </select>

                        <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-0 top-1/2 mt-1 size-4 -translate-y-1/2 text-charcoal/50' />
                    </div>
                    {errorText('topic')}
                </div>

                <div className='flex flex-col sm:col-span-2'>
                    {label('message', 'Your message')}
                    <textarea
                        id={fieldId('message')}
                        rows={5}
                        value={values.message}
                        onChange={update('message')}
                        required
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={describedBy('message')}
                        className={cn(fieldClass(Boolean(errors.message)), 'h-auto min-h-36 resize-y py-3')}
                    />
                    {errorText('message')}
                </div>
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor={`${baseId}-company`}>
                    Company
                </label>

                <input ref={honeypotRef} id={`${baseId}-company`} type='text' name='company' tabIndex={-1} autoComplete='off' />
            </div>

            {serverError && (
                <p role='alert' className='mt-8 rounded-[4px] border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
                    {serverError}
                </p>
            )}

            <button
                type='submit'
                disabled={status === 'submitting'}
                className='mt-10 inline-flex h-14 items-center justify-center gap-3 rounded-full bg-forest px-8 text-[12px] font-semibold uppercase tracking-[0.16em] text-bone transition-colors hover:bg-pine disabled:cursor-wait disabled:opacity-70'
            >
                {status === 'submitting' ? (
                    <>
                        <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                        Sending
                    </>
                ) : (
                    <>
                        Send message
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                    </>
                )}
            </button>
        </form>
    )
}
